'use client';

import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ImageCarouselProps {
  images: string[];
}

export function ImageCarousel({ images }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!images || images.length === 0) return null;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  // Single image
  if (images.length === 1) {
    return (
      <>
        <Card 
          className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
          style={{ backgroundColor: 'hsl(240, 10%, 3.9%)' }}
          onClick={() => setIsModalOpen(true)}
        >
          <img 
            src={images[0]}
            alt="Output"
            className="w-full h-auto max-h-[400px] object-contain"
          />
        </Card>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl xl:max-w-5xl max-h-[80vh] sm:max-h-[85vh] p-2 sm:p-4" style={{ backgroundColor: 'hsl(240, 10%, 3.9%)' }}>
            <img 
              src={images[0]}
              alt="Output"
              className="w-full h-full max-h-[72vh] sm:max-h-[77vh] object-contain"
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Multiple images
  return (
    <>
      <Card className="overflow-hidden" style={{ backgroundColor: 'hsl(240, 10%, 3.9%)' }}>
        <div className="relative group">
          <img 
            src={images[currentIndex]}
            alt={`Output ${currentIndex + 1}`}
            className="w-full h-auto max-h-[400px] object-contain cursor-pointer"
            onClick={() => setIsModalOpen(true)}
          />

          {/* Navigation buttons */}
          <Button
            variant="secondary"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handlePrevious}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="secondary"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Image counter */}
          <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 text-white text-xs rounded">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Dots indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-white w-4' 
                    : 'bg-white/60 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        </div>
      </Card>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl xl:max-w-5xl max-h-[80vh] sm:max-h-[85vh] p-2 sm:p-4" style={{ backgroundColor: 'hsl(240, 10%, 3.9%)' }}>
          <div className="relative">
            <img 
              src={images[currentIndex]}
              alt={`Output ${currentIndex + 1}`}
              className="w-full h-full max-h-[72vh] sm:max-h-[77vh] object-contain"
            />
            
            {images.length > 1 && (
              <>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                  onClick={handlePrevious}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  onClick={handleNext}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 text-white text-sm rounded">
                  {currentIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
